import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    useAppDispatch,
} from '../../../../base';
import CandidateForm from '../../../CandidateForm';
import {
    ApplyNowFormValues,
    IJobApplication,
} from '../../../../common';
import { jobApplicationActions } from '../../../../actions';
import * as yup from 'yup';

interface EditCandidateInfoModalProps {
    open: boolean;
    onClose: () => void;
    jobApplication: IJobApplication;
}

interface IEditCandidateForm {
    [ApplyNowFormValues.firstName]: string;
    [ApplyNowFormValues.lastName]: string;
    [ApplyNowFormValues.phoneNumber]: string;
    [ApplyNowFormValues.email]: string;
}

const EditCandidateInfoModal = (props: EditCandidateInfoModalProps) => {
    const { open, onClose, jobApplication } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validationSchema = yup.object().shape({
        [ApplyNowFormValues.firstName]: yup
            .string()
            .required(t('validation.required')),
        [ApplyNowFormValues.lastName]: yup
            .string()
            .required(t('validation.required')),
        [ApplyNowFormValues.phoneNumber]: yup
            .string()
            .required(t('validation.required'))
            .matches(/^[0-9]+$/, t('validation.numbersOnly')),
        [ApplyNowFormValues.email]: yup
            .string()
            .required(t('validation.required'))
            .email(t('validation.invalidEmail')),
    });

    const { control, handleSubmit, reset } = useForm<IEditCandidateForm>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            [ApplyNowFormValues.firstName]: jobApplication.firstName,
            [ApplyNowFormValues.lastName]: jobApplication.lastName,
            [ApplyNowFormValues.phoneNumber]: jobApplication.phone || '',
            [ApplyNowFormValues.email]: jobApplication.email,
        },
    });

    useEffect(() => {
        if (open) {
            reset({
                [ApplyNowFormValues.firstName]: jobApplication.firstName,
                [ApplyNowFormValues.lastName]: jobApplication.lastName,
                [ApplyNowFormValues.phoneNumber]: jobApplication.phone || '',
                [ApplyNowFormValues.email]: jobApplication.email,
            });
        }
    }, [open, jobApplication, reset]);

    const onSubmit = async (data: IEditCandidateForm) => {
        setIsSubmitting(true);
        try {
            await dispatch(
                jobApplicationActions.updateCandidateInfo({
                    jobApplicationId: jobApplication.id,
                    firstName: data[ApplyNowFormValues.firstName],
                    lastName: data[ApplyNowFormValues.lastName],
                    phone: data[ApplyNowFormValues.phoneNumber],
                    email: data[ApplyNowFormValues.email],
                })
            );
            onClose();
        } catch (error) {
            console.error('Failed to update candidate info:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('jobApplicationPage.editCandidateInfo')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} pt={2}>
                    <CandidateForm
                        control={control}
                        name={ApplyNowFormValues.firstName}
                        lastName={ApplyNowFormValues.lastName}
                        phoneNumber={ApplyNowFormValues.phoneNumber}
                        email={ApplyNowFormValues.email}
                    />
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    disabled={isSubmitting}
                >
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditCandidateInfoModal;
